/* =====================================================================
 * DMWidgetModal — 위젯 전용 다이렉트 메시지 모달
 *
 * - 위젯 페이지에서 DM을 라우팅 없이 모달로 표시
 * - 모바일 UX처럼 목록 -> 스레드 단일 패널 전환
 * - 메일 모달과 동일한 드래그/핀/투명도 옵션 지원
 * ===================================================================== */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { keyframes } from 'styled-components';
import { ArrowLeft, MessageCircle, MessageSquare } from 'lucide-react';
import { Modal } from '@auw/workspace-ui';
import { EmptyState } from '@auw/workspace-ui';
import { useDMStore } from './hooks/useDMStore';
import { useDMConversationStore } from '@auw/stores';
import { useUserStore } from '@auw/stores';
import DMAgentList from './DMAgentList';
import DMChatThread from './DMChatThread';
import DMUserChatThread from './DMUserChatThread';
import DMSettingsModal from './DMSettingsModal';
import type { Conversation } from '@auw/workspace-utils/types/dm';
import type { DMAgentType } from '@auw/workspace-utils/types/dm';

type ViewDirection = 'forward' | 'back';

interface DMWidgetModalProps {
  open: boolean;
  onClose: () => void;
  modalDraggable?: boolean;
  modalPinnable?: boolean;
  modalDefaultPinned?: boolean;
  modalInitialOffsetX?: number;
  modalInitialOffsetY?: number;
  modalOpacityAdjustable?: boolean;
  modalDefaultOpacity?: number;
  modalDefaultWidth?: number;
  modalDefaultHeight?: number;
  modalMinWidth?: number;
  modalCloseOnBackdrop?: boolean;
  modalDimBackdrop?: boolean;
  modalPassthroughBackdrop?: boolean;
  modalCenterPaddingLeft?: number;
  modalCenterPaddingRight?: number;
  mountMode?: 'viewport' | 'parent';
  hostBounds?: { width: number; height: number };
  modalOnSizeChange?: (size: { w: number; h: number }) => void;
  modalOnOffsetChange?: (offset: { x: number; y: number }) => void;
  modalOnPinnedChange?: (pinned: boolean) => void;
  modalOnOpacityChange?: (opacity: number) => void;
  modalLayerZIndex?: number;
  modalOnActivate?: () => void;
}

export const DMWidgetModal: React.FC<DMWidgetModalProps> = ({
  open,
  onClose,
  modalDraggable = false,
  modalPinnable = false,
  modalDefaultPinned = false,
  modalInitialOffsetX = 0,
  modalInitialOffsetY = 0,
  modalOpacityAdjustable = false,
  modalDefaultOpacity = 1,
  modalDefaultWidth = 420,
  modalDefaultHeight = 360,
  modalMinWidth = 420,
  modalCloseOnBackdrop = true,
  modalDimBackdrop = true,
  modalPassthroughBackdrop = false,
  modalCenterPaddingLeft = 0,
  modalCenterPaddingRight = 0,
  mountMode = 'viewport',
  hostBounds,
  modalOnSizeChange,
  modalOnOffsetChange,
  modalOnPinnedChange,
  modalOnOpacityChange,
  modalLayerZIndex,
  modalOnActivate,
}) => {
  const { t } = useTranslation('common');
  const { activeAgent, loadUnreadCounts, setActiveAgent } = useDMStore();
  const { activeConversation, conversations, setActiveConversation, loadConversations } = useDMConversationStore();
  const currentUserId = useUserStore((s) => s.user?.userId || s.user?.email || '');

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [activeView, setActiveView] = useState<'list' | 'detail'>('list');
  const [viewDirection, setViewDirection] = useState<ViewDirection>('forward');

  useEffect(() => {
    if (!open) return;
    setActiveView('list');
    setViewDirection('forward');
    setSettingsOpen(false);
    setActiveAgent(null);
    void setActiveConversation(null);
    void loadUnreadCounts();
    void loadConversations();
  }, [open, loadConversations, loadUnreadCounts, setActiveAgent, setActiveConversation]);

  const activeConv = useMemo(
    () => conversations.find((c) => c.conversation_id === activeConversation) ?? null,
    [conversations, activeConversation],
  );

  const memberNames = useMemo(() => {
    if (!activeConv?.members) return {};
    const map: Record<string, string> = {};
    for (const m of activeConv.members) {
      if (m.name) {
        map[m.user_id] = m.name;
      } else if (m.email) {
        map[m.user_id] = m.email.split('@')[0];
      } else {
        map[m.user_id] = m.user_id.includes('@')
          ? m.user_id.split('@')[0]
          : m.user_id.slice(0, 8);
      }
    }
    return map;
  }, [activeConv]);

  const memberPictures = useMemo(() => {
    if (!activeConv?.members) return {};
    const map: Record<string, string | null> = {};
    for (const m of activeConv.members) {
      map[m.user_id] = m.picture ?? null;
    }
    return map;
  }, [activeConv]);

  const showAgentThread = !!activeAgent && !activeConversation;
  const showUserThread = !!activeConversation && !!activeConv;
  const detailTitle = useMemo(() => {
    if (showAgentThread) {
      return activeAgent === 'system_app' ? t('dm.appMessage') : t('dm.agentConversation');
    }
    if (!showUserThread || !activeConv) return t('dm.conversation');
    if (activeConv.type === 'group') {
      return activeConv.name || t('dm.groupChat');
    }
    const other = activeConv.members?.find((m) => m.user_id !== currentUserId);
    if (!other) return t('dm.directChat');
    if (other.name) return other.name;
    if (other.email) return other.email.split('@')[0];
    return other.user_id.includes('@') ? other.user_id.split('@')[0] : other.user_id;
  }, [activeAgent, activeConv, currentUserId, showAgentThread, showUserThread]);

  const enterDetail = useCallback(() => {
    setViewDirection('forward');
    setActiveView('detail');
  }, []);

  const handleBackToList = useCallback(() => {
    setViewDirection('back');
    setActiveView('list');
  }, []);

  const handleSelectAgent = useCallback((_agentType: DMAgentType) => {
    enterDetail();
  }, [enterDetail]);

  const handleSelectConversation = useCallback((_conv: Conversation) => {
    enterDetail();
  }, [enterDetail]);

  const handleConversationOpened = useCallback((_convId: string) => {
    enterDetail();
  }, [enterDetail]);

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        title={t('dm.directMessage')}
        titleIcon={<MessageSquare size={18} />}
        width={`${modalDefaultWidth}px`}
        resizable
        defaultHeight={modalDefaultHeight}
        minWidth={modalMinWidth}
        minHeight={360}
        draggable={modalDraggable}
        pinnable={modalPinnable}
        defaultPinned={modalDefaultPinned}
        initialOffsetX={modalInitialOffsetX}
        initialOffsetY={modalInitialOffsetY}
        opacityAdjustable={modalOpacityAdjustable}
        defaultOpacity={modalDefaultOpacity}
        closeOnBackdrop={modalCloseOnBackdrop}
        dimBackdrop={modalDimBackdrop}
        passthroughBackdrop={modalPassthroughBackdrop}
        centerPaddingLeft={modalCenterPaddingLeft}
        centerPaddingRight={modalCenterPaddingRight}
        mountMode={mountMode}
        hostBounds={hostBounds}
        bodyPadding="0 12px 12px"
        onSizeChange={modalOnSizeChange}
        onOffsetChange={modalOnOffsetChange}
        onPinnedChange={modalOnPinnedChange}
        onOpacityChange={modalOnOpacityChange}
        layerZIndex={modalLayerZIndex}
        onActivate={modalOnActivate}
      >
        <ModalBody>
          <Viewport>
            <AnimatedView
              key={`${activeView}-${viewDirection}`}
              $direction={viewDirection}
            >
              {activeView === 'list' ? (
                <ListView>
                  <DMAgentList
                    onSettingsClick={() => setSettingsOpen(true)}
                    onSelectConversation={handleSelectConversation}
                    onSelectAgent={handleSelectAgent}
                    onConversationOpened={handleConversationOpened}
                    activeConversationId={activeConversation}
                    pageSidebarOpen
                    onTogglePageSidebar={() => {}}
                    showPageSidebarToggle={false}
                    showWorkspaceSidebarToggle={false}
                    flatStyle
                    surfaceOpacity={modalDefaultOpacity}
                    titleText={t('dm.conversationsAndAgents')}
                  />
                </ListView>
              ) : (
                <DetailView>
                  <DetailTopBar>
                    <BackBtn
                      type="button"
                      onClick={handleBackToList}
                      title={t('dm.backToList')}
                      aria-label={t('dm.backToList')}
                    >
                      <ArrowLeft size={16} />
                    </BackBtn>
                    <DetailTopTitle>{detailTitle}</DetailTopTitle>
                  </DetailTopBar>
                  <DetailThreadArea>
                    {showAgentThread ? (
                      <DMChatThread agentType={activeAgent} transparentInput />
                    ) : showUserThread ? (
                      <DMUserChatThread
                        conversation={activeConv}
                        memberNames={memberNames}
                        memberPictures={memberPictures}
                        transparentInput
                      />
                    ) : (
                      <EmptyWrap>
                        <EmptyState
                          icon={(
                            <EmptyIconWrap>
                              <MessageCircle size={34} strokeWidth={1.5} />
                            </EmptyIconWrap>
                          )}
                          title={t('dm.noConversationSelected')}
                          description={t('dm.selectFromList')}
                        />
                      </EmptyWrap>
                    )}
                  </DetailThreadArea>
                </DetailView>
              )}
            </AnimatedView>
          </Viewport>
        </ModalBody>
      </Modal>

      <DMSettingsModal
        isOpen={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        layerZIndex={typeof modalLayerZIndex === 'number' ? modalLayerZIndex + 1 : undefined}
      />
    </>
  );
};

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

const slideInFromRight = keyframes`
  from { opacity: 0; transform: translateX(24px); }
  to { opacity: 1; transform: translateX(0); }
`;

const slideInFromLeft = keyframes`
  from { opacity: 0; transform: translateX(-24px); }
  to { opacity: 1; transform: translateX(0); }
`;

const Viewport = styled.div`
  position: relative;
  flex: 1;
  min-height: 460px;
  overflow: hidden;
`;

const AnimatedView = styled.div<{ $direction: ViewDirection }>`
  height: 100%;
  min-height: 0;
  animation: ${({ $direction }) => ($direction === 'forward' ? slideInFromRight : slideInFromLeft)} 230ms
    cubic-bezier(0.22, 1, 0.36, 1);
`;

const ListView = styled.div`
  height: 100%;
  min-height: 0;
`;

const DetailView = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
`;

const DetailTopBar = styled.div`
  height: 44px;
  min-height: 44px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 10px 0 8px;
  border-bottom: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)'};
  background: transparent;
`;

const DetailTopTitle = styled.div`
  min-width: 0;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DetailThreadArea = styled.div`
  flex: 1;
  min-height: 0;
`;

const BackBtn = styled.button`
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  padding: 0;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.textMuted};
  background: transparent;
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    color: ${({ theme }) => theme.colors.textPrimary};
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)'};
  }
`;

const EmptyWrap = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const EmptyIconWrap = styled.div`
  color: ${({ theme }) => theme.colors.textMuted};
  opacity: 0.45;
`;

export default DMWidgetModal;
